import React, { useState, useEffect } from 'react';
import { Grid, Button, TextField, Backdrop, CircularProgress } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import MuiDialogContent from '@material-ui/core/DialogContent';
import Autocomplete from '@material-ui/lab/Autocomplete';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import LockIcon from '@material-ui/icons/Lock';
import DeleteIcon from '@material-ui/icons/Delete';
import { useSnackbar } from 'notistack';

import Api from '../../../Api';
import Modal from '../../../components/Modal/Modal';

const DialogContent = withStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    maxHeight: 'calc(100vh - 200px) !important'
  }
}))(MuiDialogContent);

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1000,
    color: '#fff'
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    color: '#607d8b'
  },
  sectionDirect: {
    marginTop: theme.spacing(2),
    backgroundColor: '#eef5ff',
    border: '1px solid #cfe0ff',
    borderRadius: 8,
    overflow: 'hidden'
  },
  sectionInherited: {
    marginTop: theme.spacing(2),
    backgroundColor: '#f4f5f7',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  subheaderDirect: {
    color: '#1565c0',
    fontWeight: 600,
    backgroundColor: 'transparent'
  },
  subheaderInherited: {
    color: '#607d8b',
    fontWeight: 600,
    backgroundColor: 'transparent'
  }
}));

// folder: the folder whose grants are managed. clients: list from GET /clients.
const GrantsManager = ({ handleClose, visibility, folder, clients }) => {
  const { enqueueSnackbar } = useSnackbar();
  const styles = useStyles();

  const [direct, setDirect] = useState([]);
  const [inherited, setInherited] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState(null); // null => toda la empresa
  const [loading, setLoading] = useState(false);

  const loadGrants = () => {
    if (!folder) {
      return;
    }
    Api.get('/drive_folder_grants/effective?folder_id=' + folder.id)
      .then(function(response) {
        const data = response.data.data || {};
        setDirect(data.direct || []);
        setInherited(data.inherited || []);
      })
      .catch(function() {
        setDirect([]);
        setInherited([]);
      });
  };

  useEffect(() => {
    if (visibility && folder) {
      loadGrants();
      setSelectedClient(null);
      setSelectedPlant(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility, folder]);

  const handleCloseDismiss = () => {
    handleClose();
  };

  const handleAddGrant = () => {
    if (!selectedClient) {
      return;
    }
    setLoading(true);
    Api.post('/drive_folder_grants', {
      folder_id: folder.id,
      client_id: selectedClient.id,
      plant_id: selectedPlant ? selectedPlant.id : null
    })
      .then(function() {
        setLoading(false);
        setSelectedClient(null);
        setSelectedPlant(null);
        loadGrants();
        enqueueSnackbar('Cliente habilitado exitosamente!', {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'success'
        });
      })
      .catch(function(error) {
        setLoading(false);
        var message = 'Error de conexion, contacta a soporte';
        if (error.response) {
          const data = error.response.data || {};
          if (data.code === 3306) {
            message = 'Este cliente ya tiene ese acceso en esta carpeta.';
          } else if (data.code === 3307) {
            message = 'La sucursal seleccionada no pertenece a ese cliente.';
          } else {
            message = data.message || 'Ocurrio un error, intenta nuevamente.';
          }
        }
        enqueueSnackbar(message, {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'error'
        });
      });
  };

  const handleRemoveGrant = grant => {
    setLoading(true);
    Api.delete('/drive_folder_grants/' + grant.id)
      .then(function() {
        setLoading(false);
        loadGrants();
        enqueueSnackbar('Acceso removido exitosamente!', {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'success'
        });
      })
      .catch(function() {
        setLoading(false);
        enqueueSnackbar('Ocurrio un error, intenta nuevamente.', {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'error'
        });
      });
  };

  // Direct + inherited access, as { client_id, plant_id } (plant_id null = toda la empresa).
  const allAccess = direct.concat(inherited);
  // A company-level grant (direct or inherited) covers every plant of that client.
  const companyCovered = clientId => allAccess.some(g => Number(g.client_id) === Number(clientId) && g.plant_id == null);
  const coveredPlantIds = clientId =>
    allAccess.filter(g => Number(g.client_id) === Number(clientId) && g.plant_id != null).map(g => Number(g.plant_id));

  // A client is offered while it has no company-level access (it may still get the whole
  // company or a plant that is not covered yet).
  const availableClients = (clients || []).filter(c => !companyCovered(c.id));
  const availablePlants = selectedClient
    ? (selectedClient.plants || []).filter(p => coveredPlantIds(selectedClient.id).indexOf(Number(p.id)) === -1)
    : [];
  const selectedClientHasPlant = selectedClient ? (selectedClient.plants || []).length > 0 : false;

  const handleChangeClient = value => {
    setSelectedClient(value);
    setSelectedPlant(null);
  };

  const grantLabel = item => {
    const clientName = item.client ? item.client.name : 'Cliente #' + item.client_id;
    if (item.plant_id == null) {
      return clientName + ' (Toda la empresa)';
    }
    return clientName + ' — ' + (item.plant ? item.plant.name : 'Sucursal #' + item.plant_id);
  };

  return (
    <div>
      <Modal handleClose={handleCloseDismiss} visibility={visibility} title={'Accesos de ' + (folder ? folder.name : '')} maxWidth="sm">
        <DialogContent dividers>
          <div className={styles.hint}>
            <InfoIcon fontSize="small" style={{ marginRight: 8 }} />
            <Typography variant="body2">El acceso otorgado se hereda a todas las subcarpetas y archivos de esta carpeta. Si no se elige sucursal, el acceso es para toda la empresa.</Typography>
          </div>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={selectedClientHasPlant ? 5 : 9}>
              <Autocomplete
                options={availableClients}
                getOptionLabel={option => option.name}
                value={selectedClient}
                onChange={(event, value) => handleChangeClient(value)}
                renderInput={params => <TextField {...params} variant="outlined" label="Cliente" />}
              />
            </Grid>
            {selectedClientHasPlant ? (
              <Grid item xs={4}>
                <Autocomplete
                  key={selectedClient.id}
                  options={availablePlants}
                  getOptionLabel={option => option.name}
                  value={selectedPlant}
                  onChange={(event, value) => setSelectedPlant(value)}
                  noOptionsText="Sin sucursales disponibles"
                  renderInput={params => (
                    <TextField {...params} variant="outlined" label="Sucursal (opcional)" placeholder="Toda la empresa" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
            ) : null}
            <Grid item xs={3}>
              <Button disabled={!selectedClient} onClick={handleAddGrant} color="primary" variant="contained" fullWidth>
                Agregar
              </Button>
            </Grid>
          </Grid>

          <div className={styles.sectionDirect}>
            <List dense subheader={<ListSubheader disableSticky className={styles.subheaderDirect}>Acceso directo a esta carpeta</ListSubheader>}>
              {direct.length > 0 ? (
                direct.map(grant => (
                  <ListItem key={grant.id}>
                    <ListItemText primary={grantLabel(grant)} />
                    <Tooltip title="Quitar acceso">
                      <Button onClick={() => handleRemoveGrant(grant)} style={{ color: '#e91e63' }}>
                        <DeleteIcon />
                      </Button>
                    </Tooltip>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primaryTypographyProps={{ color: 'textSecondary' }} primary="Ningún cliente tiene acceso directo a esta carpeta." />
                </ListItem>
              )}
            </List>
          </div>

          {inherited.length > 0 ? (
            <div className={styles.sectionInherited}>
              <List dense subheader={<ListSubheader disableSticky className={styles.subheaderInherited}>Acceso heredado (porque tiene acceso a una carpeta superior)</ListSubheader>}>
                {inherited.map(item => (
                  <ListItem key={'inh-' + item.client_id + '-' + (item.plant_id == null ? 'all' : item.plant_id)}>
                    <ListItemIcon style={{ minWidth: 36 }}>
                      <LockIcon fontSize="small" style={{ color: '#78909c' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={grantLabel(item)}
                      secondary={'Ya tiene acceso por la carpeta «' + item.source_folder_name + '»'}
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          ) : null}
        </DialogContent>
      </Modal>
      <Backdrop className={styles.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default GrantsManager;
